
// ─── Base URL ────────────────────────────────────────────────────

const RAW_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();

const CLEAN_BASE_URL = /^https?:\/\//.test(RAW_BASE_URL)
  ? RAW_BASE_URL.replace(/\/+$/, '')
  : '';

const POINTS_AT_SELF =
  typeof window !== 'undefined' &&
  CLEAN_BASE_URL.startsWith(window.location.origin);

const BASE_URL = POINTS_AT_SELF ? '' : CLEAN_BASE_URL;

if (!BASE_URL && process.env.NODE_ENV === 'production') {
  throw new Error(
    'REACT_APP_API_URL is not set. Refusing to run in production without it.'
  );
}

// Production API
// Set REACT_APP_API_URL in your production environment.

// Local development API
const RESOLVED_BASE_URL =
  BASE_URL || 'http://localhost:8000/api';

if (process.env.NODE_ENV !== 'production') {
  console.log(
    '[api.js] RAW ENV:',
    JSON.stringify(process.env.REACT_APP_API_URL),
    '| USING:',
    RESOLVED_BASE_URL
  );
}

const DEFAULT_TIMEOUT_MS = 15000;


// ─── Auth Event Bus ──────────────────────────────────────────────

function emitAuthExpired() {
  window.dispatchEvent(new CustomEvent('auth:expired'));
}


// ─── CSRF Helper ─────────────────────────────────────────────────

function getCsrfToken() {
  const match = document.cookie.match(
    /(?:^|;\s*)csrftoken=([^;]+)/
  );

  return match ? decodeURIComponent(match[1]) : null;
}

function withCsrfHeader(headers = {}, method = 'GET') {
  const needsCsrf = ![
    'GET',
    'HEAD',
    'OPTIONS',
    'TRACE'
  ].includes(method.toUpperCase());

  if (!needsCsrf) return headers;

  const csrfToken = getCsrfToken();

  return csrfToken
    ? { ...headers, 'X-CSRFToken': csrfToken }
    : headers;
}


// ─── Error Message Helper ────────────────────────────────────────

function extractErrorMessage(errorData, fallback) {
  if (!errorData || typeof errorData !== 'object') {
    return fallback;
  }

  if (typeof errorData.detail === 'string') {
    return errorData.detail;
  }

  if (typeof errorData.message === 'string') {
    return errorData.message;
  }

  if (typeof errorData.error === 'string') {
    return errorData.error;
  }

  const first = Object.values(errorData).flat()[0];

  if (typeof first === 'string') {
    return first;
  }

  return fallback;
}


// ─── Fetch With Timeout ──────────────────────────────────────────

async function fetchWithTimeout(
  url,
  options = {},
  timeoutMs = DEFAULT_TIMEOUT_MS
) {
  const controller = new AbortController();

  const timer = setTimeout(
    () => controller.abort(),
    timeoutMs
  );

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('Request timed out');
      timeoutErr.isTimeout = true;
      throw timeoutErr;
    }

    throw err;
  } finally {
    clearTimeout(timer);
  }
}


// ─── Token Refresh ───────────────────────────────────────────────

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const res = await fetchWithTimeout(
        `${RESOLVED_BASE_URL}/auth/token/refresh/`,
        {
          method: 'POST',
          headers: withCsrfHeader(
            { 'Content-Type': 'application/json' },
            'POST'
          ),
          credentials: 'include'
        }
      );

      return res.ok;
    } catch (err) {
      console.error('Token refresh failed:', err);
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}


// ─── Main API Fetch ──────────────────────────────────────────────

async function apiFetch(endpoint, options = {}) {
  const method = options.method || 'GET';

  const headers = withCsrfHeader(
    {
      'Content-Type': 'application/json',
      ...options.headers
    },
    method
  );

  const fetchOpts = {
    ...options,
    headers,
    credentials: 'include'
  };

  let response = await fetchWithTimeout(
    `${RESOLVED_BASE_URL}${endpoint}`,
    fetchOpts
  );

  // Do not refresh tokens for authentication endpoints.
  const isAuthEndpoint = [
    '/auth/login/',
    '/auth/register/',
    '/auth/token/refresh/'
  ].includes(endpoint);

  // Refresh only for protected API requests.
  if (response.status === 401 && !isAuthEndpoint) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      // Retry the original request once.
      response = await fetchWithTimeout(
        `${RESOLVED_BASE_URL}${endpoint}`,
        fetchOpts
      );
    } else {
      // Notify the application without swallowing
      // the original API response.
      emitAuthExpired();
    }
  }

  // Preserve Django's actual error response.
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    const err = new Error(
      extractErrorMessage(errorData, 'API error')
    );

    err.response = {
      status: response.status,
      data: errorData
    };

    throw err;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}


// ─── Multipart Form Data ─────────────────────────────────────────

async function apiFetchForm(
  endpoint,
  formData,
  method = 'POST'
) {
  const headers = withCsrfHeader({}, method);

  const response = await fetchWithTimeout(
    `${RESOLVED_BASE_URL}${endpoint}`,
    {
      method,
      headers,
      body: formData,
      credentials: 'include'
    },
    60000
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    const err = new Error(
      extractErrorMessage(errorData, 'Upload error')
    );

    err.response = {
      status: response.status,
      data: errorData
    };

    throw err;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}


// ─── Query String Helper ─────────────────────────────────────────

function toQueryString(params = {}) {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== ''
    )
  );

  const query = new URLSearchParams(cleaned).toString();

  return query ? `?${query}` : '';
}


// ─── Authentication API ──────────────────────────────────────────

export const authAPI = {
  login: (email, password) =>
    apiFetch('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }),

  register: (data) =>
    apiFetch('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  logout: async () => {
    try {
      await apiFetch('/auth/logout/', {
        method: 'POST'
      });
    } finally {
      emitAuthExpired();
    }
  },

  getProfile: () =>
    apiFetch('/auth/profile/'),

  updateProfile: (data) =>
    apiFetch('/auth/profile/', {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  changePassword: (data) =>
    apiFetch('/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
};


// ─── Products API ────────────────────────────────────────────────

export const productsAPI = {
  getAll: (params = {}) =>
    apiFetch(`/products/${toQueryString(params)}`),

  getBySlug: (slug) =>
    apiFetch(`/products/${encodeURIComponent(slug)}/`),

  getNewArrivals: () =>
    apiFetch('/products/new_arrivals/'),

  getJewelryAndAccessories: () =>
    apiFetch('/products/jewelry_and_accessories/'),

  getFeatured: () =>
    apiFetch('/products/featured/'),

  getByCategory: (categorySlug) =>
    apiFetch(
      `/products/${toQueryString({
        category__slug: categorySlug
      })}`
    ),

  search: (query) =>
    apiFetch(
      `/products/${toQueryString({
        search: query
      })}`
    ),

  addReview: (productId, data) =>
    apiFetch(
      `/products/${encodeURIComponent(productId)}/reviews/`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    ),

  // Admin
  create: (formData) =>
    apiFetchForm('/admin/products/', formData, 'POST'),

  update: (id, formData) =>
    apiFetchForm(
      `/admin/products/${encodeURIComponent(id)}/`,
      formData,
      'PATCH'
    ),

  delete: (id) =>
    apiFetch(
      `/admin/products/${encodeURIComponent(id)}/`,
      { method: 'DELETE' }
    ),

  toggleActive: (id) =>
    apiFetch(
      `/admin/products/${encodeURIComponent(id)}/toggle_active/`,
      { method: 'POST' }
    )
};


// ─── Categories API ──────────────────────────────────────────────

export const categoriesAPI = {
  getAll: (params = {}) =>
    apiFetch(`/categories/${toQueryString(params)}`),

  getBySlug: (slug) =>
    apiFetch(`/categories/${encodeURIComponent(slug)}/`),

  // Admin
  create: (formData) =>
    apiFetchForm('/admin/categories/', formData, 'POST'),

  update: (id, formData) =>
    apiFetchForm(
      `/admin/categories/${encodeURIComponent(id)}/`,
      formData,
      'PATCH'
    ),

  delete: (id) =>
    apiFetch(
      `/admin/categories/${encodeURIComponent(id)}/`,
      { method: 'DELETE' }
    )
};


// ─── Wishlist API ────────────────────────────────────────────────

export const wishlistAPI = {
  get: () =>
    apiFetch('/wishlist/'),

  toggle: (productId) =>
    apiFetch('/wishlist/', {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId
      })
    })
};


// ─── Cart API ────────────────────────────────────────────────────

export const cartAPI = {
  get: () =>
    apiFetch('/cart/'),

  add: (productId, quantity = 1, variantId = null) =>
    apiFetch('/cart/', {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        quantity,
        variant_id: variantId
      })
    }),

  remove: (itemId) =>
    apiFetch('/cart/', {
      method: 'DELETE',
      body: JSON.stringify({
        item_id: itemId
      })
    })
};


// ─── Orders API ──────────────────────────────────────────────────

export const ordersAPI = {
  getMyOrders: () =>
    apiFetch('/orders/'),

  getOrder: (id) =>
    apiFetch(`/orders/${encodeURIComponent(id)}/`),

  createOrder: (data) =>
    apiFetch('/orders/', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Admin
  getAll: (params = {}) =>
    apiFetch(`/admin/orders/${toQueryString(params)}`),

  getOrderAdmin: (id) =>
    apiFetch(`/admin/orders/${encodeURIComponent(id)}/`),

  updateStatus: (id, status, extra = {}) =>
    apiFetch(
      `/admin/orders/${encodeURIComponent(id)}/`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          status,
          ...extra
        })
      }
    )
};


// ─── Payments API ────────────────────────────────────────────────

export const paymentsAPI = {
  createRazorpayOrder: (data) =>
    apiFetch('/payments/create-order/', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  verifyPayment: (data) =>
    apiFetch('/payments/verify/', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // Admin
  getAll: (params = {}) =>
    apiFetch(`/admin/payments/${toQueryString(params)}`)
};


// ─── Admin API ───────────────────────────────────────────────────

export const adminAPI = {
  getDashboard: () =>
    apiFetch('/admin/dashboard/'),

  // Products
  getProducts: (params = {}) =>
    apiFetch(`/admin/products/${toQueryString(params)}`),

  createProduct: (formData) =>
    apiFetchForm('/admin/products/', formData, 'POST'),

  updateProduct: (id, formData) =>
    apiFetchForm(
      `/admin/products/${encodeURIComponent(id)}/`,
      formData,
      'PATCH'
    ),

  deleteProduct: (id) =>
    apiFetch(
      `/admin/products/${encodeURIComponent(id)}/`,
      { method: 'DELETE' }
    ),

  toggleProduct: (id) =>
    apiFetch(
      `/admin/products/${encodeURIComponent(id)}/toggle_active/`,
      { method: 'POST' }
    ),

  // Customers
  getCustomers: (params = {}) =>
    apiFetch(`/admin/customers/${toQueryString(params)}`),

  getCustomer: (id) =>
    apiFetch(`/admin/customers/${encodeURIComponent(id)}/`),

  // Reviews
  getReviews: () =>
    apiFetch('/admin/reviews/'),

  deleteReview: (id) =>
    apiFetch(
      `/admin/reviews/${encodeURIComponent(id)}/`,
      { method: 'DELETE' }
    )
};
